
import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { UserPriceLocks } from "@/components/UserPriceLocks";
import { useAuthContext } from "@/contexts/auth";
import { Lock, History, AlertCircle } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { EnhancedPriceLockCard } from './EnhancedPriceLockCard';

interface PriceLockSectionProps {
  dealerId?: string;
  dealerName?: string;
  dealerSpread?: number;
  initialMetal?: 'gold' | 'silver';
}

export function PriceLockSection({ dealerId, dealerName, dealerSpread, initialMetal }: PriceLockSectionProps) {
  const { isAuthenticated } = useAuthContext();
  const [showAuthAlert, setShowAuthAlert] = useState(!isAuthenticated);

  // Use provided dealerId/dealerName or fall back to demo values
  const effectiveDealerId = dealerId || "demo-dealer-1";
  const effectiveDealerName = dealerName || "Compro Oro Demo";

  return (
    <Card className="w-full" id="price-lock-section">
      <CardHeader>
        <CardTitle>Blocco Prezzo</CardTitle>
        <CardDescription>
          Blocca il prezzo attuale dell'oro o dell'argento per 24 ore presso {effectiveDealerName}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {showAuthAlert && !isAuthenticated && (
          <Alert variant="default" className="mb-6 bg-muted/50">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Utente non autenticato</AlertTitle>
            <AlertDescription>
              Puoi procedere senza registrarti, ma i tuoi dati di contatto saranno richiesti per ogni blocco prezzo.
              <button 
                className="text-primary font-medium ml-2 hover:underline"
                onClick={() => setShowAuthAlert(false)}
              >
                Continua
              </button>
            </AlertDescription>
          </Alert>
        )}
        
        <Tabs defaultValue="create" className="w-full">
          <TabsList className="grid grid-cols-2 mb-4">
            <TabsTrigger value="create" className="flex items-center">
              <Lock className="h-4 w-4 mr-2" />
              Blocca Prezzo
            </TabsTrigger>
            <TabsTrigger value="history" className="flex items-center">
              <History className="h-4 w-4 mr-2" />
              Cronologia
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="create">
            <EnhancedPriceLockCard 
              dealerId={effectiveDealerId}
              dealerName={effectiveDealerName}
              dealerSpread={dealerSpread}
              initialMetal={initialMetal}
            />
          </TabsContent>
          
          <TabsContent value="history">
            {isAuthenticated ? (
              <UserPriceLocks />
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Lock className="h-12 w-12 mx-auto mb-4 opacity-20" />
                <p className="mb-2">Accedi per visualizzare la cronologia dei blocchi prezzo</p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
